/**
 * RotasService.js
 *
 * Registro central de rotas autenticadas (Fase 3.1/3.2) — populado com as entidades de domínio
 * (Template/Campo/Configuracao(template)/Classificacao/Nivel/Link/Dashboard) e de plataforma
 * (Publicacao/Contrato/ControleAcesso/Auditoria/Configuracao) do LinkInsight. Mesma estrutura
 * do molde `hay-hotspot/.../RotasService.js` (Fase 1.6).
 */
import { UtilService } from 'hay-core-react';
import { codificarContexto, PARAM_CONTEXTO } from 'hay-core-react';

import DashboardPage from '../pages/Auth/Dashboard/DashboardPage';
import TemplateTemplate from '../pages/Auth/Template/TemplateTemplate';
import TemplateCampoTemplate from '../pages/Auth/TemplateCampo/TemplateCampoTemplate';
import TemplateConfiguracaoTemplate from '../pages/Auth/TemplateConfiguracao/TemplateConfiguracaoTemplate';
import ClassificacaoTemplate from '../pages/Auth/Classificacao/ClassificacaoTemplate';
import ClassificacaoNivelTemplate from '../pages/Auth/ClassificacaoNivel/ClassificacaoNivelTemplate';
import LinkTemplate from '../pages/Auth/Link/LinkTemplate';
import PublicacaoTemplate from '../pages/Auth/Publicacao/PublicacaoTemplate';
import ContratoTemplate from '../pages/Auth/Contrato/ContratoTemplate';
import ModalidadeTemplate from '../pages/Auth/Modalidade/ModalidadeTemplate';
import ConfiguracaoTemplate from '../pages/Auth/Configuracao/ConfiguracaoTemplate';
import UsuarioTemplate from '../pages/Auth/Usuario/UsuarioTemplate';
import GrupoTemplate from '../pages/Auth/Grupo/GrupoTemplate';
import PermissaoTemplate from '../pages/Auth/Permissao/PermissaoTemplate';
import GrupoPermissaoTemplate from '../pages/Auth/GrupoPermissao/GrupoPermissaoTemplate';
import UsuarioGrupoTemplate from '../pages/Auth/UsuarioGrupo/UsuarioGrupoTemplate';
import AuditoriaTemplate from '../pages/Auth/Auditoria/AuditoriaTemplate';

export const grupos = {
  plataforma: {
    label: 'main.plataforma',
    icone: 'pi pi-globe',
  },
  acesso: {
    label: 'main.acesso',
    icone: 'pi pi-lock',
  },
};

const RotaService = {
  dashboard: {
    rota: '/dashboard',
    icone: 'pi pi-home',
    identidade: 'main.dashboard',
    componente: DashboardPage,
  },
  template: {
    rota: '/template',
    icone: 'pi pi-sliders-h',
    identidade: 'main.template',
    componente: TemplateTemplate,
    camporelacao: 'descricao',
    relacoes: [
      { chave: 'templatecampo', campoPai: 'monitoramentotemplate' },
      { chave: 'templateconfiguracao', campoPai: 'monitoramentotemplate' },
    ],
  },
  templatecampo: {
    rota: '/templatecampo',
    icone: 'pi pi-link',
    identidade: 'main.template.campo',
    componente: TemplateCampoTemplate,
    camporelacao: 'nome',
    sidebarOculto: true,
  },
  templateconfiguracao: {
    rota: '/templateconfiguracao',
    icone: 'pi pi-eye',
    identidade: 'main.template.configuracao',
    componente: TemplateConfiguracaoTemplate,
    camporelacao: 'campo',
    sidebarOculto: true,
  },
  classificacao: {
    rota: '/classificacao',
    icone: 'pi pi-star',
    identidade: 'main.classificacao',
    componente: ClassificacaoTemplate,
    // O indicador (ex. "main.monitoramentotemplate.testebanda.download") identifica melhor QUAL
    // métrica está sendo classificada do que `descricao` (texto livre, às vezes repetido entre
    // classificações — ver PDF de níveis, "Facebook/Whatsapp Page Load" com a mesma descrição
    // genérica) — mais útil no breadcrumb de "Níveis" (buildRelacionados traduz o valor abaixo).
    camporelacao: 'indicador',
    relacoes: [
      { chave: 'classificacaonivel', campoPai: 'classificacao' },
    ],
  },
  classificacaonivel: {
    rota: '/classificacaonivel',
    icone: 'pi pi-flag',
    identidade: 'main.classificacao.nivel',
    componente: ClassificacaoNivelTemplate,
    camporelacao: 'descricao',
    sidebarOculto: true,
  },
  link: {
    rota: '/link',
    icone: 'pi pi-share-alt',
    identidade: 'main.link',
    componente: LinkTemplate,
    camporelacao: 'identificador',
  },

  // ── Plataforma — acessível só por quem entra pela publicação principal ─────
  publicacao: {
    rota: '/publicacao',
    icone: 'pi pi-globe',
    identidade: 'main.publicacao',
    componente: PublicacaoTemplate,
    camporelacao: 'descricao',
    grupo: 'plataforma',
    requiresAcessoGeral: true,
  },
  contrato: {
    rota: '/contrato',
    icone: 'pi pi-building',
    identidade: 'main.contrato',
    componente: ContratoTemplate,
    camporelacao: 'razaosocial',
    grupo: 'plataforma',
    requiresAcessoGeral: true,
  },
  // Catálogo global de planos (ver api/models/Modalidade.js, naofiltrarpublicacao ausente de
  // propósito — as 5 linhas ficam sob a publicação principal, então só quem acessa por ela
  // enxerga/edita) — mesmo padrão de restrição de Publicacao/Contrato acima.
  modalidade: {
    rota: '/modalidade',
    icone: 'pi pi-wallet',
    identidade: 'main.modalidade',
    componente: ModalidadeTemplate,
    camporelacao: 'nome',
    grupo: 'plataforma',
    requiresAcessoGeral: true,
  },
  // Configuracao NÃO exige publicação principal — cada tenant edita a própria.
  configuracao: {
    rota: '/configuracao',
    icone: 'pi pi-cog',
    identidade: 'main.configuracao',
    componente: ConfiguracaoTemplate,
    camporelacao: 'descricao',
    grupo: 'plataforma',
  },

  // ── Controle de Acesso ──────────────────────────────────────────────────────
  // Cada tenant gerencia o próprio time (Usuario/Grupo/UsuarioGrupo/GrupoPermissao) — só
  // `Permissao` (catálogo global de controller+action, `naofiltrarpublicacao:true` no backend)
  // fica restrito ao principal (`requiresAcessoGeral`).
  usuario: {
    rota: '/usuario',
    icone: 'pi pi-user',
    identidade: 'main.usuario',
    componente: UsuarioTemplate,
    camporelacao: 'nome',
    grupo: 'acesso',
    relacoes: [
      { chave: 'usuariogrupo', campoPai: 'usuario' },
    ],
  },
  grupo: {
    rota: '/grupo',
    icone: 'pi pi-tags',
    identidade: 'main.grupo',
    componente: GrupoTemplate,
    camporelacao: 'nome',
    grupo: 'acesso',
    relacoes: [
      { chave: 'grupopermissao', campoPai: 'grupo' },
    ],
  },
  permissao: {
    rota: '/permissao',
    icone: 'pi pi-key',
    identidade: 'main.permissao',
    componente: PermissaoTemplate,
    grupo: 'acesso',
    requiresAcessoGeral: true,
  },
  grupopermissao: {
    rota: '/grupopermissao',
    icone: 'pi pi-shield',
    identidade: 'main.grupopermissao',
    componente: GrupoPermissaoTemplate,
    grupo: 'acesso',
    sidebarOculto: true,
  },
  usuariogrupo: {
    rota: '/usuariogrupo',
    icone: 'pi pi-link',
    identidade: 'main.usuariogrupo',
    componente: UsuarioGrupoTemplate,
    grupo: 'acesso',
    sidebarOculto: true,
  },
  auditoria: {
    rota: '/auditoria',
    icone: 'pi pi-hammer',
    identidade: 'main.auditoria',
    componente: AuditoriaTemplate,
    grupo: 'acesso',
    requiresAcessoGeral: true,
  },
};

export function rotasPermitidas(permissoes) {
  return Object.fromEntries(
    Object.entries(RotaService).filter(([controller, cfg]) => {
      if (cfg.requiresAcessoGeral && !permissoes?.permiteacessogeral) return false;
      const ctrl = permissoes?.[controller];
      if (!ctrl) return false;
      return ctrl.entidade?.some(e => e.state === cfg.identidade + '.mostrar' && e.permitido);
    })
  );
}

export function buildRelacionados(controllerKey, permissoes, navigate, getSelected) {
  const cfg = RotaService[controllerKey];
  if (!cfg?.relacoes?.length) return [];
  const traduzir = (chave) => UtilService.traduzir(chave);
  const parentRecordAtual = getSelected?.() || null;

  return cfg.relacoes
    .filter(rel => {
      const chave = typeof rel === 'string' ? rel : rel.chave;
      return permissoes[chave]?.entidade?.some(e => e.permitido);
    })
    .map(rel => {
      const chave = typeof rel === 'string' ? rel : rel.chave;
      const campoPai = typeof rel === 'string' ? null : rel.campoPai;
      const ativo = typeof rel === 'string' ? null : rel.ativo;
      const relCfg = RotaService[chave];
      const habilitado = !parentRecordAtual || typeof ativo !== 'function' || ativo(parentRecordAtual) !== false;

      return {
        label: traduzir(relCfg.identidade + '.plural'),
        icon: relCfg.icone,
        disabled: !habilitado,
        command: () => {
          const parentRecord = getSelected?.() || null;
          // `traduzir` retorna a própria chave quando não encontra tradução (ver
          // UtilService.traduzir) — seguro pra aplicar em qualquer `camporelacao`, mesmo os que
          // são texto livre (nome/descrição de cliente etc., nunca batem com nenhuma chave e só
          // voltam inalterados). Necessário pro breadcrumb de `classificacao` (agora usa
          // `indicador`, uma chave i18n como "main.monitoramentotemplate.testebanda.download")
          // mostrar o rótulo traduzido ("Download") em vez da chave crua.
          const parentLabel = parentRecord ? traduzir(parentRecord[cfg.camporelacao] ?? String(parentRecord.id)) : null;
          const ctx = codificarContexto({
            parentId: parentRecord?.id ?? null,
            parentLabel,
            campoPai,
            parentControllerKey: controllerKey,
            parentRota: cfg.rota,
            parentIdentidade: cfg.identidade,
            parentIcone: cfg.icone,
            camporelacaoAtual: relCfg.camporelacao,
          });
          navigate({
            pathname: relCfg.rota,
            search: ctx ? { [PARAM_CONTEXTO]: ctx } : {},
          });
        },
      };
    });
}

export default RotaService;
